"use client";

import { COPILOT_DRAWER_STATE_QUERY_PARAM_KEY } from "@zapier/ai-toolkit/client";
import { AssetType } from "@zapier/canvas-state";

/**
 * Updates URLs by adding copilot drawer state synchronization and forwarding conversation ID as query parameters.
 *
 * Cross-domain copilot state sync: When the Copilot drawer in Canvas is open, we add the
 * ${COPILOT_DRAWER_STATE_QUERY_PARAM_KEY}=true query parameter to URLs for assets on different domains (interfaces,
 * agents, tables) since useCopilotDrawerState local storage doesn't cross domain boundaries.
 *
 * Conversation forwarding: If a conversationId is provided, it's always forwarded to maintain
 * conversation context across different applications, regardless of copilot state.
 *
 * @param isCopilotDrawerOpen - Whether the Copilot drawer is currently open in Canvas.
 * @param url - The target URL to update with query parameters.
 * @param assetType - The asset type to determine if copilot parameters should be added (only for cross-domain assets).
 * @param conversationId - Optional conversation ID to forward to the target URL (always forwarded if provided).
 * @returns The updated URL with copilot and/or conversation query parameters.
 */
export function updateUrlWithCopilotQueryParams({
  assetType,
  url,
  isCopilotDrawerOpen,
  conversationId,
}: {
  assetType: NonNullable<AssetType>;
  url: string;
  isCopilotDrawerOpen: boolean;
  conversationId?: string;
}): string {
  // Assets on different domains that need copilot state sync via query parameters
  // since useCopilotDrawerState local storage doesn't cross domain boundaries
  const crossDomainAssets = ["interface", "agent", "table"];

  try {
    const urlObj = new URL(url, window.location.origin);

    // Always forward conversationId if provided, regardless of copilot state
    if (conversationId) {
      urlObj.searchParams.set("conversationId", conversationId);
    }

    // Only add copilot parameters if copilot is open and asset type is on a different domain
    if (isCopilotDrawerOpen && crossDomainAssets.includes(assetType)) {
      urlObj.searchParams.set(COPILOT_DRAWER_STATE_QUERY_PARAM_KEY, "true");
    }

    return urlObj.toString();
  } catch {
    return url;
  }
}
